import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux';
import { Box, Grid, Stack, TextField, Typography, Card, IconButton, Tooltip } from '@mui/material';
import { grey } from '@mui/material/colors';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
// icons
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteIcon from "common/customIcons/delete";
import EditIcon from "common/customIcons/edit";
// utils and helpers
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from '../../../redux/slices/modalSlice';
import { updateDistributionConfig, getDistributionConfig, agingRuleList, deleteAgingRule, getAgingCategories } from "../../../redux/slices/hyperwolf/inventoryDistribution";
import { getAllCategory } from '../../../redux/slices/hyperwolf/products';
import { openSnackbar } from "../../../redux/slices/common";
// custom components
import MDButton from 'components/MDButton';
import NoDataFound from "common/component/NoDataFound";
import { defaultColDef } from "components/tables/CommonAgGridTable";
import { validateDistributionConfig } from "validations/hyperwolf/distributionmanagement";
import { showToastMsg } from "../distributionManagement/addRegionKit";
import { getPermissionsByMode } from 'utilities/common';
import { hasSectionPermission } from 'utilities/common';
import { getData } from 'utilities/common';

const AddDistributionConfigForm = () => {
  const dispatch = useDispatch();
  const mainGridApiRef = useRef("");

  const [gridApi, setGridApi] = useState(null);
  const [isChangesMade, setIsChangesMade] = useState(false);
  const [details, setDetails] = useState({ discrepancyTypes: [] })
  const [categories, setCategories] = useState([])
  const [discrepancyType, setDiscrepancyType] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [refreshRules, setRefreshRules] = useState(false)
  const [errors, setErrors] = useState({});

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const numOnly = value.replace(/[^0-9]/g, "");
    const validValue = Math.min(99999, Number(numOnly || 0));

    setDetails(prev => ({ ...prev, [name]: validValue }))
    setIsChangesMade(true)
  }

  const handleDiscrepancyTypes = () => {
    const updatedDetails = { ...details, discrepancyTypes: [...(details.discrepancyTypes || []), discrepancyType] }
    setDetails(prev => ({
      ...prev,
      discrepancyTypes: [...(prev.discrepancyTypes || []), discrepancyType]
    }));
    setDiscrepancyType("");
    handleUpdateConfigs(updatedDetails, true)
  }

  const handleRemoveDiscrepancyType = (type) => {
    if (!type) return;

    setDetails(prev => {
      const filterDiscrepancyTypes = prev.discrepancyTypes?.filter(disType => disType !== type)
      handleUpdateConfigs({ ...prev, discrepancyTypes: filterDiscrepancyTypes }, true)
      return ({ ...prev, discrepancyTypes: filterDiscrepancyTypes })
    });
  }

  const handleGetConfig = async () => {
    try {
      const response = await dispatch(getDistributionConfig()).unwrap()
      if (response?.data) {
        const { createdDate, updatedDate, ...rest } = response?.data || {};
        setDetails(rest || {});
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
    }
  }

  const handleCategoriesList = async () => {
    try {
      const response = await dispatch(getAllCategory({ limit: 200, userType: "distribution" })).unwrap()
      if (response?.values) {
        setCategories(response?.values || [])
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
    }
  }

  const handleGetAgingRules = useCallback(async (params) => {
    try {
      mainGridApiRef?.current?.api?.showLoadingOverlay();

      const response = await dispatch(agingRuleList()).unwrap();

      if (response && Array.isArray(response)) {
        params.successCallback(response, response.length);
        mainGridApiRef?.current?.api?.hideOverlay();
        if (!response.length) {
          mainGridApiRef.current?.api.showNoRowsOverlay();
        }
      } else {
        params.successCallback([], 0);
        mainGridApiRef.current?.api.showNoRowsOverlay();
      }
    } catch (error) {
      showToastMsg(error, "error")
    }
  }, []);

  const handleUpdateConfigs = async (updatedDetails, updateDiscrepancy) => {
    try {

      if (isLoading || (!isChangesMade && !updateDiscrepancy)) return;

      const requestData = updateDiscrepancy && updatedDetails ? updatedDetails : details;

      const { myErrors, isValid } = validateDistributionConfig({ ...requestData });
      if (!isValid) {
        setErrors(myErrors);
        return;
      } else {
        setErrors({});
      }

      setIsLoading(true);

      const request = {
        ...requestData,
        minProductPerRegion: requestData.minProductPerRegion || 0,
        maxProductPerRegion: requestData.maxProductPerRegion || 0,
        minProductPerBox: requestData.minProductPerBox || 0,
        maxProductPerBox: requestData.maxProductPerBox || 0,
        minQuantityPerProduct: requestData.minQuantityPerProduct || 0,
        maxQuantityPerProduct: requestData.maxQuantityPerProduct || 0,
        productOrderDays: requestData?.productOrderDays || 0,
        productExpiringDays: requestData?.productExpiringDays || 0,
        discrepancyResolutionETA: requestData.discrepancyResolutionETA || 0,
      }
      const response = await dispatch(updateDistributionConfig(request)).unwrap()
      if (response?.data) {
        showToastMsg(response?.message || "Success", "success")
      }
    } catch (error) {
      showToastMsg(error, "error")
    } finally {
      setIsLoading(false);
      setIsChangesMade(false);
    }
  }

  const handleDeleteRule = async (id) => {
    try {
      dispatch(setModalLoader(true));
      const res = await dispatch(deleteAgingRule(id)).unwrap();
      dispatch(openSnackbar({ content: res?.message || "Success", color: 'success', icon: 'check' }));
      dispatch(closeCustomModalDialog());
      setRefreshRules(prev => !prev)
    } catch (error) {
      dispatch(setModalLoader(false));
      showToastMsg(error, 'error');
    }
  };

  useEffect(() => {
    handleGetConfig();
    handleCategoriesList();
  }, []);

  useEffect(() => {
    if (gridApi) {
      const dataSource = { getRows: handleGetAgingRules };
      gridApi.setDatasource(dataSource);
    }
  }, [gridApi, handleGetAgingRules, refreshRules]);

  const columnDefs = useMemo(() => {
    return [
      {
        headerName: 'Category',
        field: 'category',
        minWidth: 120,
        valueGetter: (params) => {
          return params.data?.category?.name || "-"
        },
      },
      {
        headerName: 'Rule',
        field: 'rule',
        suppressMenu: false,
        minWidth: 180,
        flex: 1,
        cellStyle: { whiteSpace: 'normal', lineHeight: '1.5' },
        cellRenderer: (props) => (
          // props.data?.productOlderDays
          //   ? `Products older than ${props.data?.productOlderDays} days`
          //   : 
          props.data?.productExpiringDays
            ? `Products expiring in ${props.data?.productExpiringDays} days`
            : "-"
        ),
      },
      {
        headerName: 'Actions',
        field: 'action',
        cellRenderer: (props) => {
          if (!props?.data?._id) return null;
          return (
            <Box display="inline-flex" alignItems="center">
              <IconButton
                disabled={!canEdit}
                onClick={() => {
                  dispatch(openCustomModalDialog({
                    modal: 'ADD_AGING_RULE',
                    modalData: { categories: categories, rule: props?.data, setRefreshRules }
                  }))
                }}
                color="primary"
                size="small"
                variant="primary"
              >
                <EditIcon width="16px" height="16px" />
              </IconButton>
              <IconButton
                disabled={!canDelete}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(
                    openCustomModalDialog({
                      modalData: {
                        deleteHandler: handleDeleteRule,
                        deleteId: props.data?._id,
                        deleteType: "rule",
                      },
                      modal: "DELETE_MODAL",
                    })
                  );
                }}
                color="error"
                size="small"
                variant="primary"
                sx={{ marginLeft: 1 }}
              >
                <DeleteIcon width="16px" height="16px" />
              </IconButton>
            </Box>
          );
        },
        minWidth: 180,
        cellStyle: { textAlign: 'right', justifyContent: 'end' },
        headerClass: 'right-aligned-cell-header',
        pinned: 'right',
        className: 'field-actions'
      },
    ];
  }, [categories]);

  let localUser = getData("login-user-info");
  if (localUser) {
    localUser = JSON.parse(localUser);
  }

  const adminMode = localStorage.getItem("adminsMode")
    ? JSON.parse(localStorage.getItem("adminsMode"))
    : null;
  const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);

  const canCreate = hasSectionPermission(permissionsForMode, "Inventory Distribution", "Create");
  const canView = hasSectionPermission(permissionsForMode, "Inventory Distribution", "View Only");
  const canDelete = hasSectionPermission(permissionsForMode, "Inventory Distribution", "Delete");
  const canEdit = hasSectionPermission(permissionsForMode, "Inventory Distribution", "Edit/Update");


  return (
    <Box p={{ xs: 2, sm: 3, lg: 4 }}>
      {
        canView &&
        <>
          <Stack direction='row' gap={1} alignItems='center' justifyContent="space-between" mb={3}>
            <Typography variant='h4'>Distribution Configuration</Typography>
            {/* <MDButton
          variant="contained"
          color="primary"
          disabled={isLoading}
          sx={{ minWidth: '150px' }}
          onClick={handleUpdateConfigs}
        >
          {isLoading ? <CircularProgress color="light" size={22} /> : "Save"}
        </MDButton> */}
          </Stack>

          <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant='h4' fontWeight="semibold" color="#9A9AAF">Global Limits</Typography>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Typography variant='h5' fontWeight="semibold" color="#656575">
                          Default Values Per Region
                          <Tooltip arrow title={"Set the minimum and maximum number of unique products allowed in a region."}>
                            <span style={{ position: "relative", top: "-5px", left: "1px" }}>
                              <InfoOutlinedIcon style={{ width: '18px', height: '18px', fontWeight: 'bold' }} className="cur-pointer" />
                            </span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                      <Grid item md={6}>
                        <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Min Products Per Region</Typography>
                        <TextField
                          className="form-input"
                          fullWidth
                          placeholder="Min Products Per Region"
                          name="minProductPerRegion"
                          value={details.minProductPerRegion ?? ""}
                          onChange={handleChange}
                          onBlur={handleUpdateConfigs}
                        />
                      </Grid>
                      <Grid item md={6}>
                        <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Max Products Per Region</Typography>
                        <TextField
                          className="form-input"
                          fullWidth
                          placeholder="Max Products Per Region"
                          name="maxProductPerRegion"
                          value={details.maxProductPerRegion ?? ""}
                          onChange={handleChange}
                          onBlur={handleUpdateConfigs}
                          error={errors?.maxProductPerRegion}
                        />
                        {errors?.maxProductPerRegion && <p className="error">{errors?.maxProductPerRegion}</p>}
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <hr className='my-2' style={{ borderTop: "1px solid #F1F1F1", borderBottom: "none", borderLeft: "none", borderRight: "none" }} />
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant='h5' fontWeight="semibold" color="#656575">
                          Default Values Per Box
                          <Tooltip arrow title={"Set the minimum and maximum quantity of products that a box can contain."}>
                            <span style={{ position: "relative", top: "-5px", left: "1px" }}>
                              <InfoOutlinedIcon style={{ width: '18px', height: '18px', fontWeight: 'bold' }} className="cur-pointer" />
                            </span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                      <Grid item md={6}>
                        <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Min Products Per Box</Typography>
                        <TextField
                          className="form-input"
                          fullWidth
                          placeholder="Min Products Per Box"
                          name="minProductPerBox"
                          value={details.minProductPerBox ?? ""}
                          onChange={handleChange}
                          onBlur={handleUpdateConfigs}
                        />
                      </Grid>
                      <Grid item md={6}>
                        <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Max Products Per Box</Typography>
                        <TextField
                          className="form-input"
                          fullWidth
                          placeholder="Max Products Per Box"
                          name="maxProductPerBox"
                          value={details.maxProductPerBox ?? ""}
                          onChange={handleChange}
                          onBlur={handleUpdateConfigs}
                          error={errors?.maxProductPerBox}
                        />
                        {errors?.maxProductPerBox && <p className="error">{errors?.maxProductPerBox}</p>}
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <hr className='my-2' style={{ borderTop: "1px solid #F1F1F1", borderBottom: "none", borderLeft: "none", borderRight: "none" }} />
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant='h5' fontWeight="semibold" color="#656575">
                          Default Values Per Product
                          <Tooltip arrow title={"Set the minimum and maximum quantity required for each product."}>
                            <span style={{ position: "relative", top: "-5px", left: "1px" }}>
                              <InfoOutlinedIcon style={{ width: '18px', height: '18px', fontWeight: 'bold' }} className="cur-pointer" />
                            </span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                      <Grid item md={6}>
                        <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Min Quantity Per Products</Typography>
                        <TextField
                          className="form-input"
                          fullWidth
                          placeholder="Min Quantity Per Products"
                          name="minQuantityPerProduct"
                          value={details.minQuantityPerProduct ?? ""}
                          onChange={handleChange}
                          onBlur={handleUpdateConfigs}
                        />
                      </Grid>
                      <Grid item md={6}>
                        <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Max Quantity Per Products</Typography>
                        <TextField
                          className="form-input"
                          fullWidth
                          placeholder="Max Quantity Per Products"
                          name="maxQuantityPerProduct"
                          value={details.maxQuantityPerProduct ?? ""}
                          onChange={handleChange}
                          onBlur={handleUpdateConfigs}
                          error={errors?.maxQuantityPerProduct}
                        />
                        {errors?.maxQuantityPerProduct && <p className="error">{errors?.maxQuantityPerProduct}</p>}
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <hr className='my-2' style={{ borderTop: "1px solid #F1F1F1", borderBottom: "none", borderLeft: "none", borderRight: "none" }} />
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant='h5' fontWeight="semibold" color="#656575">
                          Default Aging Limits
                          <Tooltip arrow title={"Set the aging limit for products."}>
                            <span style={{ position: "relative", top: "-5px", left: "1px" }}>
                              <InfoOutlinedIcon style={{ width: '18px', height: '18px', fontWeight: 'bold' }} className="cur-pointer" />
                            </span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                      {/* <Grid item md={6}>
                    <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Products Older Than</Typography>
                    <div className='position-relative'>
                      <TextField
                        className="form-input"
                        fullWidth
                        placeholder="Products Order Days"
                        name="productOrderDays"
                        value={details.productOrderDays ?? ""}
                        onChange={handleChange}
                        onBlur={handleUpdateConfigs}
                      />
                      <span className='position-absolute right-0 top-0 h-100 d-flex jc-center align-item-center' style={{ color: "#9A9AAF", minWidth: "80px", textAlign: "center", fontSize: "14px" }}>Days</span>
                    </div>
                  </Grid> */}
                      <Grid item md={6}>
                        <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Products Expiring In</Typography>
                        <div className='position-relative'>
                          <TextField
                            className="form-input"
                            fullWidth
                            placeholder="Products Expiring Days"
                            name="productExpiringDays"
                            value={details.productExpiringDays ?? ""}
                            onChange={handleChange}
                            onBlur={handleUpdateConfigs}
                          />
                          <span className='position-absolute right-0 top-0 h-100 d-flex jc-center align-item-center' style={{ color: "#9A9AAF", minWidth: "80px", textAlign: "center", fontSize: "14px" }}>Days</span>
                        </div>
                      </Grid>
                    </Grid>
                  </Grid>

                </Grid>
              </Grid>
              <Grid item xs={12}>
                <hr className='my-2' style={{ borderTop: "1px solid #F1F1F1", borderBottom: "none", borderLeft: "none", borderRight: "none" }} />
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant='h5' fontWeight="semibold" color="#656575">Discrepancy Management</Typography>
                      </Grid>
                      <Grid item md={6}>
                        <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Discrepancy Resolution ETA</Typography>
                        <div className='position-relative'>
                          <TextField
                            className="form-input"
                            fullWidth
                            placeholder="Discrepancy Resolution ETA"
                            name="discrepancyResolutionETA"
                            value={details.discrepancyResolutionETA ?? ""}
                            onChange={handleChange}
                            onBlur={handleUpdateConfigs}
                          />
                          <span className='position-absolute right-0 top-0 h-100 d-flex jc-center align-item-center' style={{ color: "#9A9AAF", minWidth: "80px", textAlign: "center", fontSize: "14px" }}>Hours</span>
                        </div>
                        <Typography variant='body2' fontWeight="medium" color={grey[600]} mt={1}>Set the hours to resolve this discrepancy before products are marked as waste.</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <hr className='my-2' style={{ borderTop: "1px solid #F1F1F1", borderBottom: "none", borderLeft: "none", borderRight: "none" }} />
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant='h5' fontWeight="semibold" color="#656575">Discrepancy Type</Typography>
                      </Grid>
                      <Grid item md={6}>
                        <TextField
                          label=""
                          className="form-input"
                          fullWidth
                          placeholder="Add Discrepancy Type"
                          name="discrepancyType"
                          value={discrepancyType || ""}
                          onChange={e => setDiscrepancyType(e.target.value)}
                        />
                      </Grid>
                      {
                        canCreate &&
                        <Grid item md={6}>
                          <MDButton
                            className="btn-disabled"
                            sx={{ minWidth: '115px', minHeight: '47px' }}
                            type='button'
                            variant='outlined'
                            color="primary"
                            disabled={!discrepancyType}
                            onClick={handleDiscrepancyTypes}
                          >
                            Add
                          </MDButton>
                        </Grid>
                      }
                    </Grid>
                    <Grid item md={12}>
                      <div className="discrepancy-type-badge">
                        {
                          details?.discrepancyTypes?.map(type => (
                            <badge key={type} className="d-flex align-item-center justify-content-center badge-field">
                              <span>{type}</span>
                              <span className="clear-cross" onClick={() => handleRemoveDiscrepancyType(type)}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="6" height="6" viewBox="0 0 6 6" fill="none">
                                  <g clipPath="url(#clip0_17959_91869)">
                                    <path d="M3.78658 2.99958L5.61921 1.16719C5.82484 0.962648 5.82484 0.631327 5.62134 0.425716C5.41677 0.220105 5.0854 0.220105 4.87976 0.423585L4.8787 0.42465L3.04607 2.25704L1.21239 0.425716C1.00781 0.22117 0.675384 0.22117 0.470812 0.425716C0.26624 0.630261 0.26624 0.962648 0.470812 1.16719L2.30344 2.99958L0.470812 4.83197C0.26624 5.03651 0.26624 5.3689 0.470812 5.57344C0.675384 5.77799 1.00781 5.77799 1.21239 5.57344L3.04501 3.74106L4.87763 5.57344C5.08221 5.77799 5.41464 5.77799 5.61921 5.57344C5.82378 5.3689 5.82378 5.03651 5.61921 4.83197L3.78658 2.99958Z" fill="#F2F3F7" />
                                  </g>
                                  <defs>
                                    <clipPath id="clip0_17959_91869">
                                      <rect width="5.45525" height="5.45455" fill="white" transform="translate(0.317383 0.272461)" />
                                    </clipPath>
                                  </defs>
                                </svg>
                              </span>
                            </badge>
                          ))
                        }
                      </div>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              {/*  <Grid item xs={12}>
            <hr className='my-2' style={{ borderTop: "1px solid #F1F1F1", borderBottom: "none", borderLeft: "none", borderRight: "none" }} />
          </Grid>
         <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1} style={{ position: "relative", left: "-12px" }}>
              <FormControlLabel
                name='isRetailer'
                control={<Switch
                  checked={details?.isRetailer}
                  onChange={e => {
                    setDetails(prev => ({ ...prev, isRetailer: e.target.checked }));
                    handleUpdateConfigs({ ...details, isRetailer: e.target.checked }, true);
                  }}
                />}
                sx={{ m: 0 }}
              />
              <Typography variant='body2' fontWeight="medium" color={grey[600]}>Enable Retail Value</Typography>
            </Box>
          </Grid> */}
              <Grid item xs={12}>
                <hr className='my-2' style={{ borderTop: "1px solid #F1F1F1", borderBottom: "none", borderLeft: "none", borderRight: "none" }} />
              </Grid>

              {/* Aging rules */}
              <Grid item xs={12}>
                <Stack direction='row' gap={1} alignItems='center' justifyContent="space-between">
                  <Typography variant='h5' fontWeight="semibold" color="#656575">Aging Rules By Category</Typography>
                  {
                    canCreate &&
                    <MDButton
                      variant="contained"
                      sx={{ minWidth: '150px', background: "#2E2E3A !important", color: "#ffffff !important", fontSize: "14px", minHeight: "36px", padding: "7px 20px" }}
                      onClick={() => {
                        dispatch(openCustomModalDialog({
                          modal: 'ADD_AGING_RULE',
                          modalData: { categories: categories, setRefreshRules }
                        }))
                      }}
                    >
                      + Add Aging Rule
                    </MDButton>
                  }
                </Stack>
              </Grid>

              <Grid item xs={12} className="table-border-gray">
                <div
                  className="ag-theme-quartz"
                  style={{ height: "450px", width: "100%" }}
                >
                  <AgGridReact
                    ref={mainGridApiRef}
                    className="ag-grid--table"
                    pagination={false}
                    rowModelType="infinite"
                    overlayLoadingTemplate={'<span class="table-loader"></span>'}
                    columnDefs={columnDefs}
                    noRowsOverlayComponent={() => <NoDataFound />}
                    onGridReady={onGridReady}
                    headerHeight={36}
                    getRowStyle={(params) => {
                      if (params.data === undefined) {
                        return { display: "none" };
                      }
                    }}
                    rowHeight={56}
                    rowSelection="multiple"
                    suppressRowClickSelection={true}
                    defaultColDef={{ ...defaultColDef }}
                  />
                </div>
              </Grid>
            </Grid>
          </Card>
        </>
      }

    </Box>
  )
}

export default AddDistributionConfigForm
